import {
  Stack,
  Button,
  TextInput,
  Text,
  Group,
  Title,
  ButtonProps,
  Checkbox,
  Skeleton,
} from '@mantine/core';
import cn from 'clsx';
import { BiLinkExternal } from 'react-icons/bi';
import { useQuery } from '@tanstack/react-query';
import { useAccount } from 'wagmi';
import BigNumber from 'bignumber.js';
import { useState, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  GoogleReCaptchaProvider,
  GoogleReCaptcha,
} from 'react-google-recaptcha-v3';
import { useStakingIsPaused } from 'src/api/smart-contracts';
import { useMakeDeposit } from 'src/hooks/useMakeDeposit';
import { FAUCET_API_URL, NETWORK_VARS } from 'src/constants';
import { formatDecimal } from 'src/utils';
import { WalletSuspense } from 'src/components/WalletSuspense/WalletSuspense';
import { ErrorDisplay } from 'src/components/ErrorDisplay/ErrorDisplay';
import { Rows } from 'src/components/Rows/Rows';

const RECAPTCHAV3_KEY = import.meta.env.VITE_RECAPTCHAV3_KEY;

const PAUSE_FAUCET = import.meta.env.VITE_PAUSE_FAUCET === 'true';
const pausedFaucetMessage =
  import.meta.env.VITE_PAUSED_FAUCET_MESSAGE || 'Faucet is paused';
const SHOW_DISCORD = import.meta.env.VITE_FAUCET_SHOW_DISCORD === 'true';

type FaucetInfoType = {
  account: string;
  receiver: string;
  network: string;
  symbol: string;
  tier: number;
  tierAmount: string;
  skipAuth: boolean;
  alreadyClaimed: boolean;
  timeRemaining: number;
  rejectMessage?: string;
};

const fallbackFaucetInfo: FaucetInfoType = {
  account: '',
  receiver: '',
  network: '',
  symbol: '',
  tier: 0,
  tierAmount: '20000000000000000',
  skipAuth: false,
  alreadyClaimed: false,
  timeRemaining: 0,
};

function _FaucetPage() {
  const { address } = useAccount();

  const [autoDeposit, setAutoDeposit] = useState(true);
  const [token, setToken] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [claimId, setClaimId] = useState<string | null>(null);
  const [txHash, setTxHash] = useState('');
  const [depositHash, setDepositHash] = useState('');

  const { data: capacity, isLoading: isLoadingCapacity } = useQuery({
    queryKey: ['capacity'],
    queryFn: async (): Promise<{ amountLeft: number; dailyLimit: number }> => {
      const response = await fetch(`${FAUCET_API_URL}/api/capacity`);
      const body = await response.json();
      return body;
    },
    enabled: !PAUSE_FAUCET,
  });

  const faucetDepleted =
    typeof capacity !== 'undefined' && BigNumber(capacity.amountLeft).lt(5);

  const { data: faucetInfo, isLoading: isLoadingFaucetInfo } = useQuery({
    queryKey: ['faucet-info', address],
    queryFn: async (): Promise<FaucetInfoType> => {
      const response = await fetch(
        `${FAUCET_API_URL}/api/info?address=${address}`,
        {
          credentials: 'include',
        }
      );
      const body = await response.json();
      return body;
    },
    enabled: !!address && !PAUSE_FAUCET && !claimId && !faucetDepleted,
  });

  const {
    skipAuth,
    tierAmount,
    rejectMessage: faucetErrorMessage,
  } = { ...fallbackFaucetInfo, ...faucetInfo };

  const faucetDisabled = !!faucetErrorMessage;

  const amountToDeposit = faucetDisabled
    ? '0'
    : BigNumber(tierAmount).div('1e18').toString();

  const { stakingIsPaused, isLoadingStakingIsPaused } = useStakingIsPaused();

  const { data: claimStatus } = useQuery({
    queryKey: ['claim-status', claimId],
    queryFn: async (): Promise<{
      tx_hash: string;
      processed: boolean;
      error?: string;
    }> => {
      const response = await fetch(
        `${FAUCET_API_URL}/api/claim-status?id=${claimId}`,
        {
          credentials: 'include',
        }
      );
      const body = await response.json();
      return body;
    },
    refetchInterval: 3000,
    enabled: !!claimId && !txHash,
  });

  useEffect(() => {
    if (claimStatus?.processed) {
      setTxHash(claimStatus.tx_hash);
    }
  }, [claimStatus?.processed]);

  const handleRecaptchaVerify = useCallback((token: string) => {
    setToken(token);
    setError(null);
  }, []);

  const { data: authData, isLoading: isLoadingSocialAuthorized } = useQuery({
    queryKey: ['social-auth'],
    queryFn: async (): Promise<{ twitter: boolean; discord: boolean }> => {
      const response = await fetch(`${FAUCET_API_URL}/api/authorized`, {
        credentials: 'include',
      });
      const json = await response.json();
      return json;
    },
    enabled: !PAUSE_FAUCET && !faucetDepleted,
  });

  const { isDepositing, refetchBalance, refetchSimulate, writeContract } =
    useMakeDeposit({
      amount: amountToDeposit,
      forceTargetAmount: true,
      onSuccess: (hash) => {
        setDepositHash(hash);
      },
    });

  const formDisabled =
    faucetDepleted ||
    faucetDisabled ||
    isLoadingFaucetInfo ||
    !!txHash ||
    (!skipAuth && (!token || !authData?.twitter));

  const renderTwitterBtn = () => {
    const renderBtn = (btnProps?: ButtonProps) => (
      <Button type="button" variant="gradient" {...btnProps}>
        Connect Twitter
      </Button>
    );

    if (isLoadingSocialAuthorized) {
      return renderBtn({ loading: true });
    }

    return (
      <a href={`${FAUCET_API_URL}/api/auth?provider=twitter`}>{renderBtn()}</a>
    );
  };

  const renderAuth = () => {
    if (isLoadingFaucetInfo || skipAuth) {
      return null;
    }

    if (authData?.twitter) {
      return <GoogleReCaptcha onVerify={handleRecaptchaVerify} />;
    }

    return <div>{renderTwitterBtn()}</div>;
  };

  const handleSubmit = async () => {
    if (formDisabled) {
      return;
    }

    setIsProcessing(true);
    setError(null);

    const claimResponse = await fetch(`${FAUCET_API_URL}/api/claim`, {
      credentials: 'include',
      method: 'POST',
      headers: {
        ...(skipAuth ? {} : { 'recaptcha-v3-token': token! }),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        address,
      }),
    });

    const body = await claimResponse.json();

    if (!claimResponse.ok) {
      setError(body.message);
      setToken(null);
      setIsProcessing(false);
    } else {
      setClaimId(body.claim_id);

      if (autoDeposit && !stakingIsPaused) {
        let newBalance = await refetchBalance();

        while (
          BigNumber(String(newBalance.data?.value))
            .div(`1e${newBalance.data?.decimals}`)
            .lt(amountToDeposit)
        ) {
          newBalance = await refetchBalance();
        }

        const { data: config } = await refetchSimulate();

        if (config?.request) {
          setIsProcessing(false);
          writeContract(config?.request);
        } else {
          setIsProcessing(false);
        }
      } else {
        setIsProcessing(false);
      }
    }
  };

  const isPollingHash = !!claimId && !txHash;

  return (
    <Stack w={480} mx="auto" gap={24}>
      <Title order={2} fz={24}>
        Faucet
      </Title>
      <WalletSuspense buttonProps={{ mx: 'auto', display: 'block' }}>
        <form
          onSubmit={(e) => {
            e.preventDefault();

            handleSubmit();
          }}
          className={cn(
            isLoadingFaucetInfo && 'animate-pulse pointer-events-none',
            PAUSE_FAUCET && 'pointer-events-none'
          )}
        >
          <Stack gap={24}>
            <TextInput value={address} disabled label="Your address" />

            {SHOW_DISCORD &&
              !isLoadingSocialAuthorized &&
              !authData?.discord && (
                <Stack gap={12}>
                  <Text>
                    aPriori community members? Get more testnet tokens!
                  </Text>
                  <a href={`${FAUCET_API_URL}/api/auth?provider=discord`}>
                    <Button type="button" variant="gradient">
                      Connect Discord
                    </Button>
                  </a>
                  <Text fz={12} c="var(--mantine-color-main-2)">
                    Members must have lapr role or above to qualify.
                  </Text>
                </Stack>
              )}

            {renderAuth()}

            <div
              title={stakingIsPaused ? 'Staking contract is paused' : undefined}
            >
              <Checkbox
                label="Auto-Stake Received MON"
                disabled={
                  isLoadingStakingIsPaused ||
                  stakingIsPaused ||
                  isProcessing ||
                  isDepositing ||
                  isPollingHash
                }
                checked={stakingIsPaused ? false : autoDeposit}
                className={cn(isLoadingStakingIsPaused && 'animate-pulse')}
                onChange={(e) => setAutoDeposit(e.currentTarget.checked)}
              />
            </div>

            <Button
              type="submit"
              loading={isProcessing || isDepositing || isPollingHash}
              disabled={formDisabled}
              size="lg"
              w="100%"
              variant="gradient"
            >
              {PAUSE_FAUCET
                ? pausedFaucetMessage
                : faucetDepleted
                  ? 'Insufficient faucet capacity'
                  : faucetDisabled
                    ? 'This address is not eligible to claim'
                    : txHash
                      ? 'Claimed!'
                      : `Claim MON`}
            </Button>

            <Stack gap={8}>
              <Rows
                items={[
                  {
                    label: 'You can claim every',
                    value: '12 hours',
                  },
                  {
                    label: 'Daily claim capacity left',
                    value:
                      isLoadingCapacity || typeof capacity === 'undefined' ? (
                        <Skeleton w={100} h={23} />
                      ) : (
                        `${formatDecimal(capacity.amountLeft)} / ${formatDecimal(capacity.dailyLimit)} MON`
                      ),
                  },
                ]}
              />
            </Stack>

            {(error || faucetErrorMessage || claimStatus?.error) && (
              <ErrorDisplay>
                {error || faucetErrorMessage || claimStatus?.error}
              </ErrorDisplay>
            )}

            {renderTxHash(txHash)}
            {renderDepositHash(depositHash)}
          </Stack>
        </form>
      </WalletSuspense>
    </Stack>
  );
}

export function FaucetPage() {
  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={RECAPTCHAV3_KEY}
      scriptProps={{
        async: false,
        defer: false,
        appendTo: 'head',
        nonce: undefined,
      }}
    >
      <_FaucetPage />
    </GoogleReCaptchaProvider>
  );
}

function renderTxHash(txHash?: string) {
  if (!txHash) {
    return null;
  }

  return (
    <Link to={`${NETWORK_VARS.explorer}/tx/${txHash}`} target="_blank">
      <Group gap={4}>
        <Text c="var(--mantine-color-main-0)">View faucet transaction</Text>

        <BiLinkExternal size={20} color="var(--mantine-color-main-0)" />
      </Group>
    </Link>
  );
}

function renderDepositHash(depositHash?: string) {
  if (!depositHash) {
    return null;
  }
  return (
    <Link to={`${NETWORK_VARS.explorer}/tx/${depositHash}`} target="_blank">
      <Group gap={4}>
        <Text c="var(--mantine-color-main-0)">View stake transaction</Text>

        <BiLinkExternal size={20} color="var(--mantine-color-main-0)" />
      </Group>
    </Link>
  );
}
